import {
    createColumns,
    DataTableCustomized,
} from '@/components/ui/data-table-customized'
import { QUERY_KEYS } from '@/constants/reactQueryKeys'
import { useQueryWithHandler } from '@/hooks/useQueryWithHandler'
import { formatCurrency, formatNumeral } from '@/utils/function'
import dayjs from 'dayjs'
import React, { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { Typogaphy } from 'vnetwork-ui'
import { getRevenueCashFlow, getEmployeePerformances } from '../../../services'
import type {
    RevenueCashFlowItem,
    SalesTargetFilters,
    MonthlyCashFlowBreakdown,
} from '../../../types'
import type { RevenueType } from '../index'

/** Row type with dynamic month keys */
interface EmployeeAchievementRow {
    employee_id: number
    employee_name: string
    target_amount: number
    cashflow_revenue: number
    achievement_percentage: number
    [key: string]: string | number // dynamic month_YYYY-MM columns
}

interface AchievementByPaymentDateProps extends SalesTargetFilters {
    revenueTypes: RevenueType[]
}

/** Sum selected revenue types from a breakdown or employee-level data */
const sumSelectedRevenue = (
    data: {
        renewal_revenue: number
        new_sale_revenue: number
        upsell_revenue: number
    },
    types: RevenueType[]
): number => {
    let total = 0
    if (types.includes('renewal')) total += data.renewal_revenue || 0
    if (types.includes('new_sale')) total += data.new_sale_revenue || 0
    if (types.includes('upsell')) total += data.upsell_revenue || 0
    return total
}

/** Achievement table by payment date - shows per-employee KPI completion with monthly breakdown */
const AchievementByPaymentDate: React.FC<AchievementByPaymentDateProps> = ({
    revenueTypes,
    ...filters
}) => {
    const { t } = useTranslation()

    const { data: cashFlowData } = useQueryWithHandler({
        queryKey: [
            QUERY_KEYS.REPORTS_BUSINESS_TARGETS,
            'revenue-by-payment-date',
            filters.employee_id ?? [],
            filters.gte,
            filters.lte,
        ],
        queryFn: () =>
            getRevenueCashFlow({
                gte: filters.gte,
                lte: filters.lte,
                employee_ids: filters.employee_id ?? [],
            }),
        staleTime: 5 * 60 * 1000,
    })

    const { data: performanceData } = useQueryWithHandler({
        queryKey: [
            QUERY_KEYS.REPORTS_BUSINESS_TARGETS,
            'branch',
            filters.gte,
            filters.lte,
            filters.employee_id,
        ],
        queryFn: () =>
            getEmployeePerformances({
                gte: filters.gte,
                lte: filters.lte,
                employee_id: filters.employee_id,
            }),
        enabled: !!filters.gte && !!filters.lte,
    })

    // Generate month keys from gte/lte range
    const monthColumns = useMemo(() => {
        if (!filters.gte || !filters.lte) return []
        const start = dayjs(filters.gte)
        const end = dayjs(filters.lte)
        const months: string[] = []
        let current = start.startOf('month')
        while (current.isBefore(end) || current.isSame(end, 'month')) {
            months.push(current.format('YYYY-MM'))
            current = current.add(1, 'month')
        }
        return months
    }, [filters.gte, filters.lte])

    // Build rows with monthly breakdown, filtered by selected revenue types
    const employeeRows = useMemo<EmployeeAchievementRow[]>(() => {
        if (!cashFlowData || cashFlowData.length === 0) return []

        const targetMap = new Map<number, number>()
        performanceData?.employees?.forEach((emp) => {
            targetMap.set(emp.employee_id, emp.target_amount || 0)
        })

        return cashFlowData
            .map((emp: RevenueCashFlowItem) => {
                const target = targetMap.get(emp.employee_id) || 0
                const cashflow = sumSelectedRevenue(emp, revenueTypes)

                // Build monthly revenue map using only selected types
                const monthlyMap: Record<string, number> = {}
                const breakdownByMonth = new Map<
                    string,
                    MonthlyCashFlowBreakdown
                >()
                emp.monthly_breakdown?.forEach((b) => {
                    breakdownByMonth.set(b.payment_month, b)
                })

                monthColumns.forEach((m) => {
                    const b = breakdownByMonth.get(m)
                    monthlyMap[`month_${m}`] = b
                        ? sumSelectedRevenue(b, revenueTypes)
                        : 0
                })

                return {
                    employee_id: emp.employee_id,
                    employee_name: emp.employee_name,
                    target_amount: target,
                    cashflow_revenue: cashflow,
                    achievement_percentage:
                        target > 0 ? (cashflow / target) * 100 : 0,
                    ...monthlyMap,
                }
            })
            .sort((a, b) => b.cashflow_revenue - a.cashflow_revenue)
    }, [cashFlowData, performanceData, monthColumns, revenueTypes])

    // Dynamic columns: name, target, monthly..., total cashflow, achievement
    const employeeColumns = useMemo(() => {
        const cols: any[] = [
            {
                key: 'employee_name',
                title: t('employees.name'),
                width: 180,
                headerClassName: 'text-left',
                cellClassName: 'font-medium',
            },
            {
                key: 'target_amount',
                title: t('employees.performance.commitment'),
                width: 130,
                headerClassName: 'text-right',
                cellClassName: 'text-right',
                render: (value: number) => formatCurrency(value),
            },
        ]

        // Add a column per month
        monthColumns.forEach((month) => {
            cols.push({
                key: `month_${month}`,
                title: `T${dayjs(month).format('M')}`,
                width: 110,
                headerClassName: 'text-right',
                cellClassName: 'text-right',
                render: (value: number) => formatCurrency(value || 0),
            })
        })

        // Total + achievement
        cols.push(
            {
                key: 'cashflow_revenue',
                title: t('revenue_cash_flow.total_cash_collected', {
                    defaultValue: 'Tổng DT',
                }),
                width: 140,
                headerClassName: 'text-right bg-green-50',
                cellClassName: 'text-right font-semibold bg-green-50/50',
                render: (value: number) => formatCurrency(value),
            },
            {
                key: 'achievement_percentage',
                title: t('employees.performance.achievement_percentage'),
                width: 90,
                headerClassName: 'text-right',
                cellClassName: 'text-right',
                render: (value: number) => (
                    <span
                        className={`font-semibold ${
                            value >= 100
                                ? 'text-green-600'
                                : value >= 80
                                  ? 'text-yellow-600'
                                  : 'text-red-600'
                        }`}
                    >
                        {formatNumeral(Number(value.toFixed(2)))}%
                    </span>
                ),
            }
        )

        return createColumns<EmployeeAchievementRow>(cols)
    }, [t, monthColumns])

    // Footer with totals
    const employeeFooterRow = useMemo(() => {
        if (employeeRows.length === 0) return undefined

        const totalTarget = employeeRows.reduce(
            (sum, r) => sum + (r.target_amount as number),
            0
        )
        const totalCashflow = employeeRows.reduce(
            (sum, r) => sum + (r.cashflow_revenue as number),
            0
        )
        const totalAchievement =
            totalTarget > 0 ? (totalCashflow / totalTarget) * 100 : 0

        const cells: Record<string, React.ReactNode> = {
            employee_name: t('overview.total'),
            target_amount: formatCurrency(totalTarget),
        }

        // Monthly totals
        monthColumns.forEach((month) => {
            const key = `month_${month}`
            const monthTotal = employeeRows.reduce(
                (sum, r) => sum + (Number(r[key]) || 0),
                0
            )
            cells[key] = formatCurrency(monthTotal)
        })

        cells.cashflow_revenue = (
            <span className="font-bold">{formatCurrency(totalCashflow)}</span>
        )
        cells.achievement_percentage = (
            <span
                className={`font-semibold ${
                    totalAchievement >= 100
                        ? 'text-green-600'
                        : totalAchievement >= 80
                          ? 'text-yellow-600'
                          : 'text-red-600'
                }`}
            >
                {formatNumeral(Number(totalAchievement.toFixed(2)))}%
            </span>
        )

        return { cells, cellClassName: 'text-right' }
    }, [employeeRows, monthColumns, t])

    if (employeeRows.length === 0) return null

    return (
        <div className="space-y-2">
            <Typogaphy className="font-medium text-muted-foreground">
                {t('employees.performance.achievement_by_payment_date', {
                    defaultValue:
                        'Mức độ hoàn thành chỉ tiêu (theo TG thanh toán)',
                })}
            </Typogaphy>

            <DataTableCustomized
                columns={employeeColumns}
                rows={employeeRows}
                rowKey={(row) => String(row.employee_id)}
                footerRow={employeeFooterRow}
                showIndex
                size="small"
            />
        </div>
    )
}

export default AchievementByPaymentDate
